import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { fetchAdminSupportConversations, type SupportConversationThread } from '@/lib/supportChat';

function getConversationSignature(conversations: SupportConversationThread[]) {
  return conversations
    .map((conversation) => [conversation.id, conversation.last_message_at, conversation.updated_at, conversation.status].join(':'))
    .join('|');
}

export function useAdminSupportConversations(enabled = true) {
  const [conversations, setConversations] = useState<SupportConversationThread[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);
  const hasLoadedOnceRef = useRef(false);
  const refreshTimeoutRef = useRef<number | null>(null);

  const refreshConversations = useCallback(
    async (options?: { background?: boolean }) => {
      if (!enabled) {
        setConversations([]);
        setLoading(false);
        setError(null);
        hasLoadedOnceRef.current = false;
        return [] as SupportConversationThread[];
      }

      const background = options?.background ?? false;
      if (!background && !hasLoadedOnceRef.current) {
        setLoading(true);
      }
      setError(null);

      try {
        const nextConversations = await fetchAdminSupportConversations();
        setConversations((current) => {
          if (getConversationSignature(current) === getConversationSignature(nextConversations)) {
            return current;
          }
          return nextConversations;
        });
        hasLoadedOnceRef.current = true;
        return nextConversations;
      } catch (nextError) {
        const normalizedError = nextError instanceof Error ? nextError : new Error('Failed to load support conversations.');
        setError(normalizedError);
        return [] as SupportConversationThread[];
      } finally {
        setLoading(false);
      }
    },
    [enabled]
  );

  useEffect(() => {
    if (!enabled) {
      setConversations([]);
      setLoading(false);
      setError(null);
      hasLoadedOnceRef.current = false;
      return;
    }

    let cancelled = false;

    const loadConversations = async () => {
      const nextConversations = await refreshConversations();
      if (cancelled) {
        return;
      }

      if (nextConversations.length > 0) {
        hasLoadedOnceRef.current = true;
      }
    };

    const scheduleRefresh = () => {
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current);
      }

      refreshTimeoutRef.current = window.setTimeout(() => {
        void refreshConversations({ background: true });
      }, 250);
    };

    void loadConversations();

    const refreshIfActive = () => {
      if (document.visibilityState === 'visible') {
        scheduleRefresh();
      }
    };

    const interval = window.setInterval(() => {
      void refreshConversations({ background: true });
    }, 10000);

    window.addEventListener('focus', refreshIfActive);
    document.addEventListener('visibilitychange', refreshIfActive);

    const channel = supabase
      .channel('admin-support-conversations')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'support_conversations' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'support_messages' }, scheduleRefresh)
      .subscribe();

    return () => {
      cancelled = true;
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current);
      }
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshIfActive);
      document.removeEventListener('visibilitychange', refreshIfActive);
      void supabase.removeChannel(channel);
    };
  }, [enabled, refreshConversations]);

  return {
    conversations,
    loading,
    error,
    refreshConversations,
  };
}
